import {animate, group, query, state, style, transition, trigger} from '@angular/animations';
import {AsyncPipe} from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostBinding,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {MatIconModule} from '@angular/material/icon';
import {ActivatedRoute, Router} from '@angular/router';
import {Store} from '@ngrx/store';
import * as moment from 'moment-timezone';
import {filter, map, switchMap, take} from 'rxjs';
import {selectActiveUser} from 'src/app/core/store/active-user/active-user.selector';
import {GetCurrentSkedChallengeAction} from 'src/app/core/store/current-sked/current-sked.action';
import {selectChallengeById} from 'src/app/core/store/current-sked/current-sked.selector';
import {OphLoadingModule} from 'src/app/shared/design/oph-loading/oph-loading.module';
import {NumberAbbreviationPipe} from 'src/app/shared/pipes/number-abbreviation.pipe';
import {CsSharedHeaderComponent} from '../shared/header/cs-shared-header.component';
import {CsChallengesCyclesComponent} from './cycles/cs-challenges-cycles.component';
import {CsChallengesTasksComponent} from './tasks/cs-challenges-tasks.component';

const TAB_SLIDE_DURATION = '250ms ease';
const PAGE_SLIDE_DURATION = '200ms ease-out';

@Component({
  selector: 'current-sked-challenge',
  standalone: true,
  imports: [
    AsyncPipe,
    MatIconModule,
    NumberAbbreviationPipe,
    OphLoadingModule,
    CsSharedHeaderComponent,
    CsChallengesTasksComponent,
    CsChallengesCyclesComponent,
  ],
  templateUrl: './current-sked-challenge.component.html',
  styleUrl: './current-sked-challenge.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('pageSlide', [
      state('enter', style({left: '0', opacity: 1})),
      state('leave', style({left: '100%', opacity: 0})),
      state('none', style({left: '0', opacity: 1})),
      transition('void => enter', [style({left: '100%', opacity: 0}), animate(PAGE_SLIDE_DURATION)]),
      transition('void => none', []),
      transition('* => leave', animate(PAGE_SLIDE_DURATION)),
    ]),
    trigger('tabContentSlide', [
      transition(':increment', [
        group([
          query(
            ':leave',
            [
              style({position: 'absolute', width: '100%', opacity: 1, transform: 'translateX(0)'}),
              animate(TAB_SLIDE_DURATION, style({opacity: 0, transform: 'translateX(-100%)'})),
            ],
            {optional: true}
          ),
          query(
            ':enter',
            [
              style({opacity: 0, transform: 'translateX(100%)'}),
              animate(TAB_SLIDE_DURATION, style({opacity: 1, transform: 'translateX(0)'})),
            ],
            {optional: true}
          ),
        ]),
      ]),
      transition(':decrement', [
        group([
          query(
            ':leave',
            [
              style({position: 'absolute', width: '100%', opacity: 1, transform: 'translateX(0)'}),
              animate(TAB_SLIDE_DURATION, style({opacity: 0, transform: 'translateX(100%)'})),
            ],
            {optional: true}
          ),
          query(
            ':enter',
            [
              style({opacity: 0, transform: 'translateX(-100%)'}),
              animate(TAB_SLIDE_DURATION, style({opacity: 1, transform: 'translateX(0)'})),
            ],
            {optional: true}
          ),
        ]),
      ]),
    ]),
  ],
})
export class CurrentSkedChallengeComponent implements OnInit {
  @HostBinding('class') hostClasses = 'oph-feature-layout oph-feature-padding';
  @HostBinding('@pageSlide') animationState: 'enter' | 'leave' | 'none' = 'none';

  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);

  constructor() {
    const nav = this.router.getCurrentNavigation();
    this.animationState = nav?.extras?.state?.['fromInitialSelection'] ? 'enter' : 'none';
  }

  public readonly activeUser$ = this.store.select(selectActiveUser);

  private readonly challengeId = signal<string | null>(null);

  public readonly challenge = toSignal(
    this.activatedRoute.paramMap.pipe(
      map(params => params.get('id')),
      filter(id => !!id),
      switchMap(id => this.store.select(selectChallengeById(id)))
    )
  );

  public readonly challengeLoading = signal(true);
  public readonly selectedView = signal<'tasks' | 'cycles'>('tasks');
  public readonly selectedViewIndex = computed(() => (this.selectedView() === 'tasks' ? 0 : 1));

  /** Persisted state so child components resume where the user left off. */
  public readonly persistedDay = signal<moment.Moment | null>(null);
  public readonly persistedCycleKey = signal<string | null>(null);

  public readonly tabs = [
    {value: 'tasks' as const, label: 'Tasks'},
    {value: 'cycles' as const, label: 'Cycles'},
  ];

  public ngOnInit(): void {
    this.activatedRoute.paramMap
      .pipe(
        map(params => params.get('id')),
        filter(id => !!id),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(id => {
        this.challengeId.set(id);
        this.getChallenge(id);
      });
  }

  private getChallenge(id: string): void {
    this.challengeLoading.set(true);
    this.store.dispatch(
      new GetCurrentSkedChallengeAction({
        challengeId: id,
        onSuccess: () => this.challengeLoading.set(false),
        onFailure: () => this.challengeLoading.set(false),
      })
    );
  }

  public onBackClick(): void {
    this.animationState = 'leave';
    setTimeout(() => {
      this.router.navigate(['..'], {relativeTo: this.activatedRoute});
    }, 200);
  }

  public onViewChange(view: string): void {
    this.selectedView.set(view as 'tasks' | 'cycles');
  }
}
